
import { Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { recordVaultIngestPathFromRouterUrl } from '@nebula/shared-vault/data-access';
import { ingestUiStore } from '../../../../store/src/lib/ingest-ui.store';

@Component({
  selector: 'app-ingest',
  templateUrl: './ingest.component.html',
  styleUrls: ['./ingest.component.scss'],
  standalone: true,
  imports: [RouterOutlet],
})
export class IngestComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly uiStore = inject(ingestUiStore);

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => {
        const current = e.urlAfterRedirects;
        recordVaultIngestPathFromRouterUrl(current);
        if (this.isIngestChildUrl(current)) {
          this.uiStore.setCurrentRoute(this.normalizeUrl(current));
        }
      });
  }

  ngOnInit(): void {
    const bootstrap = this.normalizeUrl(this.getBootstrapUrl());
    const routerCurrent = this.normalizeUrl(this.router.url);
    if (!this.isIngestChildUrl(bootstrap)) return;

    // Regla fuerte: la URL real del navegador al entrar (F5/navegación) manda.
    // Si algún sync previo la cambió a `projects`, se restaura aquí inmediatamente.
    if (routerCurrent && routerCurrent !== bootstrap) {
      this.uiStore.setCurrentRoute(bootstrap);
      void this.router.navigateByUrl(bootstrap, { replaceUrl: true });
      return;
    }

    const current = bootstrap;
    const stored = this.normalizeUrl(this.uiStore.currentRoute());
    const isGeneric = this.isGenericIngestUrl(current);
    if (isGeneric && stored && this.isIngestChildUrl(stored) && stored !== current) {
      void this.router.navigateByUrl(stored, { replaceUrl: true });
      return;
    }
    this.uiStore.setCurrentRoute(current);
  }

  private isIngestChildUrl(url: string): boolean {
    const clean = this.normalizeUrl(url);
    return /^\/layout\/vault\/vault(\/.*)?$/.test(clean);
  }

  private isGenericIngestUrl(url: string): boolean {
    const clean = this.normalizeUrl(url);
    // "projects" también se trata como entrada genérica para poder restaurar
    // una ruta previa de trabajo (ej. upload o projects/:id/upload) si el router
    // cae ahí durante bootstrap o cambio de módulo.
    return (
      clean === '/layout/vault/vault' ||
      clean === '/layout/vault' ||
      clean === '/layout/vault/vault/projects' ||
      clean === '/layout/vault/projects'
    );
  }

  private normalizeUrl(url: string): string {
    return (url || '').split('?')[0].split('#')[0].replace(/\/+$/, '');
  }

  private getBootstrapUrl(): string {
    try {
      if (typeof window !== 'undefined' && window.location) {
        const p = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`;
        if (p) return p;
      }
    } catch {
      // fallback below
    }
    return this.router.url;
  }
}
